import {useState} from "react";
import {Box, Button, HStack, Span, Stack, Text} from "@chakra-ui/react";
import type {ReviewAction} from "platformed-client/types";
import {InnerDialogProps, withDialog} from "../../../state/withDialog";
import {DialogBody, DialogFooter, DialogHeader, DialogTitle} from "../../../components/ui/dialog";
import {Alert} from "../../../components/ui/alert";
import {Radio, RadioGroup} from "../../../components/ui/radio";
import {OwnerAvatar} from "../../../components/OwnerAvatar";
import {usePromiseState} from "../../../hooks/promiseState";
import {getOwnerNames} from "../../../utils/owners";
import {removalImpact, type RemovalScope} from "./reviewCandidates";
import {ReviewStatusText} from "./ReviewerRow";
import T from "platformed-components/i18n/T";

export type RemoveReviewersDialogProps = {
  /** Name of the page being edited, so the copy can say what is losing its reviewers. */
  targetName: string;
  removed: ReviewAction[];
  /** Additions applied in the same gesture; only changes the wording. */
  addedCount: number;
  /** 0 hides the scope choice — there is nothing to cascade to. */
  descendantPageCount: number;
  onConfirm: (scope: RemovalScope) => Promise<void>;
};

/**
 * Confirms taking reviewers off a role. Withdrawing a request the reviewer may already be
 * working on is confirmed too, not just discarding a completed review; the wording differs.
 */
export const RemoveReviewersDialog = withDialog(
  ({
    onClose,
    targetName,
    removed,
    addedCount,
    descendantPageCount,
    onConfirm,
  }: InnerDialogProps & RemoveReviewersDialogProps) => {
    const [scope, setScope] = useState<RemovalScope>("page");
    const {pending, completed} = removalImpact(removed);

    const [removing, confirm] = usePromiseState(async () => {
      await onConfirm(scope);
      onClose();
    }, [onConfirm, scope, onClose]);

    const summary =
      completed.length === 0 ? (
        <T keyName="tprm.reviews.remove_dialog.pending_summary" params={{count: pending.length, page: targetName}} />
      ) : pending.length === 0 ? (
        <T
          keyName="tprm.reviews.remove_dialog.completed_summary"
          params={{count: completed.length, page: targetName}}
        />
      ) : (
        <T
          keyName="tprm.reviews.remove_dialog.mixed_summary"
          params={{pending: pending.length, completed: completed.length, page: targetName}}
        />
      );

    return (
      <>
        <DialogHeader>
          <DialogTitle>
            {removed.length === 1 ? (
              <T
                keyName="c.Remove_name[parameterized]"
                params={{name: getOwnerNames(removed[0].reviewer.owner_payload)[0]}}
              />
            ) : (
              <T keyName="c.Remove_reviewers[count]" params={{count: removed.length}} />
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap={4}>
            <Text fontSize="sm" color="fg.muted">
              {summary}{" "}
              {addedCount > 0 && (
                <T keyName="tprm.reviews.remove_dialog.also_requesting[count]" params={{count: addedCount}} />
              )}
            </Text>

            {/* Unlabelled: the title names the action and the summary above names its effect. */}
            <Box borderWidth="1px" borderColor="border" rounded="md" overflow="hidden">
              <Stack gap={0}>
                {removed.map(review => (
                  <HStack
                    key={review.reviewer.owner_id}
                    gap={3}
                    px={3}
                    py={2}
                    borderBottomWidth="1px"
                    borderColor="border"
                    _last={{borderBottomWidth: 0}}
                    align="flex-start"
                  >
                    <Stack flex={1} minW={0} gap={0.5}>
                      {/* Status shares the identity's row so the two sit on one line, whatever
                          height the comment below adds. */}
                      <HStack gap={2}>
                        <OwnerAvatar owner={review.reviewer.owner_payload} />
                        <Span flex={1} />
                        <ReviewStatusText status={review.review_status} />
                      </HStack>
                      {review.comment && (
                        <Text fontSize="xs" color="fg.muted" fontStyle="italic" lineClamp={2}>
                          {review.comment}
                        </Text>
                      )}
                    </Stack>
                  </HStack>
                ))}
              </Stack>
            </Box>

            {descendantPageCount > 0 && (
              <Stack gap={2}>
                {/* The two options state the choice; a question above them would only repeat it. */}
                <RadioGroup size="sm" value={scope} onValueChange={e => setScope(e.value as RemovalScope)}>
                  <Stack gap={2}>
                    <Radio value="page">
                      <T keyName="c.Remove_from_this_page_only" />
                    </Radio>
                    <Radio value="subtree">
                      <T
                        keyName="tprm.reviews.remove_dialog.subtree_option[count]"
                        params={{count: descendantPageCount}}
                      />
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Stack>
            )}

            {/* Withdrawing an unanswered request discards no decision, so this has nothing to
                say about it. */}
            {completed.length > 0 && (
              <Alert status="info" title={<T keyName="tprm.reviews.remove_dialog.activity_note" />} />
            )}

            {removing.lastError != null && (
              <Alert status="error" title={<T keyName="tprm.reviews.remove_dialog.failed" />}>
                {`${removing.lastError}`}
              </Alert>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={removing.inProgress}>
            <T keyName="c.Keep_them" />
          </Button>
          <Button colorPalette="red" onClick={confirm} loading={removing.inProgress} disabled={removing.inProgress}>
            {completed.length > 0 ? (
              <T keyName="c.Dismiss_completed_reviews[count]" params={{count: completed.length}} />
            ) : (
              <T keyName="c.Withdraw_requests[count]" params={{count: pending.length}} />
            )}
          </Button>
        </DialogFooter>
      </>
    );
  },
  {size: "md"},
);
